import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ExternalLink, Star, BookOpen } from "lucide-react";
import { getBook } from "@/lib/api";
import Newsletter from "@/components/Newsletter";

export default function BookDetail() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getBook(slug).then(setBook).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="book-not-found">
        <p className="text-zinc-400">Book not found.</p>
        <Link to="/books" className="text-amber-500 mt-4 inline-block">Back to books</Link>
      </div>
    );
  }

  if (!book) return <div className="pt-40 pb-40 text-center text-zinc-600">Loading…</div>;

  return (
    <div data-testid="book-detail-page">
      <section className="relative overflow-hidden">
        <img src={book.cover_image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 blur-2xl scale-110" />
        <div className="absolute inset-0 bg-zinc-950/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20">
          <Link to="/books" data-testid="back-to-books" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-500 transition-colors mb-12">
            <ArrowLeft size={16} /> All books
          </Link>

          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="relative mx-auto lg:mx-0 max-w-md w-full"
            >
              <div className="absolute -inset-6 bg-amber-500/10 blur-3xl" />
              <img src={book.cover_image} alt={book.title} data-testid="book-detail-cover" className="relative w-full border border-zinc-800 shadow-2xl" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="text-[11px] tracking-accent uppercase text-amber-500 border border-amber-500/40 px-2 py-1">{book.genre}</span>
                <span className="text-xs text-zinc-500">{book.release_year}</span>
              </div>
              {book.series && <p className="text-sm text-zinc-500 mb-2">{book.series}</p>}
              <h1 data-testid="book-detail-title" className="font-display text-4xl sm:text-5xl font-black tracking-tight">{book.title}</h1>
              <p className="mt-4 text-lg italic text-amber-500/90">{book.tagline}</p>
              <div className="mt-8 space-y-4 text-zinc-300 leading-relaxed text-lg">
                {book.description.split("\n\n").map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>

              {book.buy_links?.length > 0 && (
                <div className="mt-10">
                  <p className="text-xs tracking-accent uppercase text-zinc-500 mb-4 flex items-center gap-2"><BookOpen size={14} /> Get the book</p>
                  <div className="flex flex-wrap gap-3">
                    {book.buy_links.map((l) => (
                      <a
                        key={l.label}
                        href={l.url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`buy-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="inline-flex items-center gap-2 bg-amber-500 text-zinc-950 font-bold text-sm px-6 py-3 hover:bg-amber-400 transition-colors"
                      >
                        {l.label} <ExternalLink size={14} />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {book.praise?.length > 0 && (
                <div className="mt-12 space-y-5">
                  {book.praise.map((p, i) => (
                    <div key={i} className="flex gap-3 items-start border-l-2 border-amber-500 pl-4">
                      <Star size={16} className="text-amber-500 mt-1 shrink-0" />
                      <p className="text-sm italic text-zinc-400">{p}</p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      <Newsletter />
    </div>
  );
}
